<template>
  <Modal position="end" ref="modalRef" @after-close="form.resetValidation()">
    <template #header>Add unit</template>

    <template #body>
      <FormGroup class="form">
        <FormField
          label="Unit number"
          hint="The address, manager and team are the building's. The rest is asked next, as for any listing."
          :error="form.fieldErrors('unitNumber')"
        >
          <Input v-model="form.fields.unitNumber" name="unitNumber" maxlength="10" data-testid="unit-number">
            <template #prefix>#</template>
          </Input>
        </FormField>
      </FormGroup>
    </template>

    <template #footer>
      <Button variant="ghost" @click="modalRef?.close()">Cancel</Button>
      <Button :loading="form.submitting" :disabled="form.validation.$invalid" data-testid="add-unit" @click="add">
        Add unit
      </Button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { helpers, maxLength, required } from '@vuelidate/validators';
import { AxiosError } from 'axios';
import { ref, watch } from 'vue';
import { useRouter } from 'vue-router';

import {
  Button,
  FormField,
  FormGroup,
  Input,
  Modal,
  extractErrorMessage,
  serverErrorCode,
  useAlertsStore,
  useForm,
} from '@tallo/design-system';
import { buildingErrorCodes, useCreateUnit } from '@tallo/investor/property';

import { PropertyOnboardingStep } from '../../property-onboarding/configs/stages.enum';
import { getPropertyOnboardingStepRouteName } from '../../property-onboarding/utils/get-property-onboarding-step-route-name.util';

const props = defineProps<{ buildingId: string }>();

// The building answers the address and type steps, so a unit's wizard starts at its specifications
const firstUnitStep = PropertyOnboardingStep.SPECIFICATIONS;

const router = useRouter();
const { mutateAsync: createUnit } = useCreateUnit();
const modalRef = ref<InstanceType<typeof Modal>>();

const form = useForm<{ unitNumber: string }>(
  { unitNumber: '' },
  {
    unitNumber: {
      required: helpers.withMessage('Give the unit its number', required),
      maxLength: helpers.withMessage('Up to 10 characters', maxLength(10)),
    },
  },
);

function open() {
  form.fields.unitNumber = '';
  modalRef.value?.show();
}

async function add() {
  let unitId: string;

  try {
    const unit = await form.submit(() =>
      createUnit({ buildingId: props.buildingId, unitNumber: form.fields.unitNumber.trim() }),
    );
    unitId = unit.id;
  } catch (error) {
    if (serverErrorCode(error) === buildingErrorCodes.unitNumberTaken) {
      form.setFieldError('unitNumber', extractErrorMessage(error, 'Another unit in this building has that number'));
    } else if (error instanceof AxiosError && !error.response?.data?.validationErrors) {
      // Someone who can't change the building gets its access dialog, which holds this toast back
      useAlertsStore().catchError(error);
    }

    return;
  }

  modalRef.value?.close();
  await router.push({ name: getPropertyOnboardingStepRouteName(firstUnitStep), params: { propertyId: unitId } });
}

// The conflict is about the number as typed; the form keeps a server error until it is cleared
watch(
  () => form.fields.unitNumber,
  () => form.clearValidationErrors(),
);

defineExpose({ open });
</script>

<style scoped lang="scss">
.form {
  width: 100%;
}
</style>
